// context/AuthProvider.jsx
"use client";

import React, {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { auth, db, doc, getDoc } from "@/config/firebaseConfig";
import {
  onIdTokenChanged,
  signOut,
  signInWithCustomToken,
} from "firebase/auth";
import { onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { usePathname } from "next/navigation";

import useBackendUrl from "@/hooks/useBackendUrl";
import { useAsyncRoutePush } from "@/hooks/useAsyncPush";
import Script from "next/script";
import Cookies from "js-cookie";
import finishDealerLogin from "@/helpers/finishDealerLogin";
import { isNoTrackingRoute } from "@/constants/noTrackingRoutes";
import { ensurePersistence } from "@/app/helpers/persistence";
import {
  clearDealerLogoutInProgress,
  isDealerLogoutInProgress,
  markDealerLogoutInProgress,
} from "@/helpers/dealerAuthSession";

const CACHE_KEY = (mail) => `userData:${mail}`;

// How we treat the "null" gap emitted by Firebase on cred change:
const AUTH_GAP_MAX_RETRIES = 3;
const AUTH_GAP_DELAY_MS = 250;
const SESSION_COOKIE_MAX_RETRIES = 3;
const SESSION_COOKIE_RETRY_DELAY_MS = 500;

// 600ms grace: let Firebase hydrate from local persistence before we fall back to cookie→custom-token
const PERSISTENCE_GRACE_MS = 600;

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const hasDealerFrontCookie = () =>
  typeof document !== "undefined" &&
  document.cookie.includes("__session_dealer_front=");

const clearDealerFrontCookie = () => {
  if (typeof document === "undefined") return;
  document.cookie =
    "__session_dealer_front=; Path=/; SameSite=Lax; Max-Age=0; " +
    "Expires=Thu, 01 Jan 1970 00:00:00 GMT";
};

const wait = (delayMs) =>
  new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });

const warn = (...args) =>
  console.warn("%c[AuthProvider]", "color:#b54708", ...args);
const err = (...args) =>
  console.error("%c[AuthProvider]", "color:#d1242f", ...args);

export default function AuthProvider({
  children,
  initialUser = null,
  initialCustomToken = null,
}) {
  const pathname = usePathname();
  const blockTracking = isNoTrackingRoute(pathname);

  // Firebase client user (actual signed-in user on this tab)
  const [user, setUser] = useState(null);
  // Firestore AuktionUsers doc (SSR bootstrap)
  const [userData, setUserData] = useState(initialUser);

  // Flags
  const [loading, setLoading] = useState(!initialUser);
  const [ready, setReady] = useState(!!initialUser);
  const [error, setError] = useState(null);
  const [claims, setClaims] = useState(null);

  // 🚦 Gate Firestore listeners elsewhere with this: true only when client can safely query
  const [queryReady, setQueryReady] = useState(false);

  const api = useBackendUrl();
  const push = useAsyncRoutePush();

  // Consent
  const [isEssentialChecked] = useState(!!Cookies.get("essential") || true);
  const [isAnalyticalChecked] = useState(!!Cookies.get("analytical") || false);
  const [isMarketingChecked] = useState(!!Cookies.get("marketing") || false);

  // Listener refs / guards
  const unsubAuthRef = useRef(null);
  const unsubUserDocRef = useRef(null);
  const prevUidRef = useRef(null);
  const ensuredFrontCookieRef = useRef(false);
  const activeUserDocUidRef = useRef(null);
  const authEventEpochRef = useRef(0);
  const userSessionEpochRef = useRef(0);
  const authRestorePromiseRef = useRef(null);
  const cookieRetryTimerRef = useRef(null);

  const resolveEmail = async (cur) => {
    const fromAuth = cur?.email;
    const fromProvider = cur?.providerData?.map((p) => p?.email).find(Boolean);
    let fromToken;
    try {
      const t = await cur.getIdTokenResult();
      fromToken = t?.claims?.email;
    } catch {}
    return (
      fromAuth ||
      fromProvider ||
      fromToken ||
      (initialUser?.uid === cur?.uid ? initialUser?.email : "") ||
      ""
    ).toLowerCase();
  };

  // Derived: can this client query protected data?
  const canQuery = !!user && (claims?.role === "dealer" || !claims);

  useEffect(() => {
    if (!blockTracking || typeof window === "undefined") return;

    try {
      if (window.fbq) window.fbq("consent", "revoke");
    } catch {}

    try {
      if (window.clarity) window.clarity("consent", false);
    } catch {}
  }, [blockTracking]);

  const restoreAuthFromSession = () => {
    if (auth.currentUser) return Promise.resolve(true);
    if (isDealerLogoutInProgress()) return Promise.resolve(false);
    if (authRestorePromiseRef.current) return authRestorePromiseRef.current;

    const restorePromise = (async () => {
      try {
        await ensurePersistence(auth);
      } catch (e) {
        warn("setPersistence during auth restore failed:", e?.code || e?.message);
      }

      for (let attempt = 1; attempt <= AUTH_GAP_MAX_RETRIES; attempt += 1) {
        if (auth.currentUser) return true;
        if (isDealerLogoutInProgress()) return false;
        if (attempt > 1) {
          await wait(AUTH_GAP_DELAY_MS * attempt);
        }

        try {
          const res = await fetch(`${api}/auction/session-to-custom-token`, {
            method: "GET",
            credentials: "include",
            cache: "no-store",
          });

          if (res.status === 401 || res.status === 403) return false;
          if (!res.ok) {
            warn(`auth restore #${attempt}: backend returned`, res.status);
            continue;
          }

          const { token } = await res.json();
          if (!token) continue;
          if (auth.currentUser) return true;
          if (isDealerLogoutInProgress()) return false;

          await signInWithCustomToken(auth, token);
          if (isDealerLogoutInProgress()) {
            await signOut(auth).catch(() => {});
            return false;
          }
          if (auth.currentUser) return true;
        } catch (e) {
          warn(`auth restore #${attempt} failed:`, e?.code || e?.message);
        }
      }

      return false;
    })().finally(() => {
      if (authRestorePromiseRef.current === restorePromise) {
        authRestorePromiseRef.current = null;
      }
    });

    authRestorePromiseRef.current = restorePromise;
    return restorePromise;
  };

  // Give Firebase a short hydration window, then use the server session as a
  // bounded fallback. The auth listener below performs the same restore
  // immediately for an SSR-hydrated user that emits a transient null state.
  useEffect(() => {
    if (auth.currentUser || !hasDealerFrontCookie()) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      const restored = await restoreAuthFromSession();
      if (!cancelled && !restored && !auth.currentUser) {
        warn("fallback bootstrap could not restore the dealer session");
      }
    }, PERSISTENCE_GRACE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  // Main auth listener (no dependency on userData)
  useEffect(() => {
    let cancelled = false;

    const detachUserDocListener = () => {
      if (!unsubUserDocRef.current) return;
      try {
        unsubUserDocRef.current();
      } catch {}
      unsubUserDocRef.current = null;
    };

    const applySignedOutState = () => {
      userSessionEpochRef.current += 1;
      activeUserDocUidRef.current = null;
      prevUidRef.current = null;
      ensuredFrontCookieRef.current = false;
      detachUserDocListener();
      setUser(null);
      setUserData(null);
      setClaims(null);
      setQueryReady(false);
      setLoading(false);
      setReady(true);
    };

    const isCurrentUserEvent = (eventEpoch, sessionEpoch, uid) =>
      !cancelled &&
      authEventEpochRef.current === eventEpoch &&
      userSessionEpochRef.current === sessionEpoch &&
      auth.currentUser?.uid === uid;

    // cleanup previous listeners if any
    if (unsubAuthRef.current) {
      try {
        unsubAuthRef.current();
      } catch {}
      unsubAuthRef.current = null;
    }
    detachUserDocListener();
    activeUserDocUidRef.current = null;

    const startAuthListener = async () => {
      try {
        await ensurePersistence(auth);
      } catch (e) {
        warn("setPersistence failed:", e?.code || e?.message);
      }

      if (cancelled) return;

      const persistedUser = auth.currentUser;
      if (
        persistedUser &&
        (!initialUser || persistedUser.uid !== initialUser.uid)
      ) {
        // Firebase LOCAL persistence is newer than a missing/stale SSR cookie.
        // Force both dealer session cookies to be recreated from this user.
        clearDealerFrontCookie();
        ensuredFrontCookieRef.current = false;
      }

      const shouldUseInitialToken =
        initialCustomToken &&
        !isDealerLogoutInProgress() &&
        !auth.currentUser;

      if (shouldUseInitialToken) {
        try {
          await signInWithCustomToken(auth, initialCustomToken);
          if (isDealerLogoutInProgress()) {
            await signOut(auth).catch(() => {});
          }
        } catch (e) {
          warn("initial custom-token bootstrap failed:", e?.code || e?.message);
        }
      }

      if (cancelled) return;

      unsubAuthRef.current = onIdTokenChanged(auth, async (cur) => {
        if (cancelled) return;
        const eventEpoch = ++authEventEpochRef.current;

        if (!cur) {
          if (
            !isDealerLogoutInProgress() &&
            hasDealerFrontCookie()
          ) {
            // A LOCAL-persistence account switch can briefly emit null before
            // the new user reaches this tab. Prefer that newer browser state
            // over restoring the older server cookie immediately.
            await wait(AUTH_GAP_DELAY_MS);
            if (
              cancelled ||
              authEventEpochRef.current !== eventEpoch ||
              auth.currentUser
            ) {
              return;
            }
            if (isDealerLogoutInProgress()) {
              applySignedOutState();
              return;
            }

            const restored = await restoreAuthFromSession();
            if (
              cancelled ||
              authEventEpochRef.current !== eventEpoch ||
              auth.currentUser
            ) {
              return;
            }
            if (restored) return;

            clearDealerFrontCookie();
            fetch(`${api}/auction/logout`, {
              method: "POST",
              credentials: "include",
              cache: "no-store",
              keepalive: true,
            }).catch(() => {});
          }

          if (
            cancelled ||
            authEventEpochRef.current !== eventEpoch ||
            auth.currentUser
          ) {
            return;
          }
          applySignedOutState();
          return;
        }

        // A UID change invalidates every pending read and snapshot from the
        // previous account. A same-UID token refresh only updates claims.
        const previousUid = prevUidRef.current;
        const uidChanged = previousUid !== cur.uid;
        const accountChanged = Boolean(
          (previousUid && previousUid !== cur.uid) ||
            (initialUser?.uid && initialUser.uid !== cur.uid)
        );
        if (uidChanged) {
          userSessionEpochRef.current += 1;
          activeUserDocUidRef.current = null;
          ensuredFrontCookieRef.current = false;
          prevUidRef.current = cur.uid;
          detachUserDocListener();
          setQueryReady(false);
          if (accountChanged) {
            // Never render account-scoped data from the previous dealer while
            // the newly persisted Firebase user is still being resolved.
            setUser(null);
            setUserData(null);
            setClaims(null);
            setLoading(true);
            setReady(false);
          }
        }
        const sessionEpoch = userSessionEpochRef.current;

        try {
          const tokenResult = await cur.getIdTokenResult(false);
          if (!isCurrentUserEvent(eventEpoch, sessionEpoch, cur.uid)) return;
          setClaims(tokenResult?.claims ?? null);
        } catch (e) {
          if (!isCurrentUserEvent(eventEpoch, sessionEpoch, cur.uid)) return;
          warn("getIdTokenResult failed:", e?.code || e?.message);
        }

        // The existing document listener remains valid across ordinary token
        // refreshes; rebuilding it would create overlapping async callbacks.
        if (!uidChanged && activeUserDocUidRef.current === cur.uid) {
          setLoading(false);
          setReady(true);
          return;
        }

        const email = await resolveEmail(cur);
        if (!isCurrentUserEvent(eventEpoch, sessionEpoch, cur.uid)) return;

        setUser(email ? { ...cur, email } : cur);

        if (!email) {
          warn("no email on auth user; skipping Firestore attach");
          setReady(true);
          setLoading(false);
          setQueryReady(true);
          return;
        }

        const ref = doc(db, "AuktionUsers", email);
        const cachedKey = CACHE_KEY(email);

        try {
          const cached = sessionStorage.getItem(cachedKey);
          if ((accountChanged || !userData) && cached && cached !== "null") {
            setUserData(JSON.parse(cached));
            setLoading(false);
            setReady(true);
          }
        } catch {}

        let exists = false;
        try {
          const snap = await getDoc(ref);
          if (!isCurrentUserEvent(eventEpoch, sessionEpoch, cur.uid)) return;
          exists = snap.exists();
          if (exists) {
            const data = snap.data();
            setUserData(data);
            try {
              sessionStorage.setItem(cachedKey, JSON.stringify(data));
            } catch {}
            setLoading(false);
            setReady(true);
            setQueryReady(true);
          }
        } catch (e) {
          if (!isCurrentUserEvent(eventEpoch, sessionEpoch, cur.uid)) return;
          err("getDoc failed:", e?.code || e?.message);
        }

        if (!exists) {
          try {
            const base = {
              createdAt: serverTimestamp(),
              mailTags: { online: true },
            };
            await setDoc(ref, base, { merge: true });
            if (!isCurrentUserEvent(eventEpoch, sessionEpoch, cur.uid)) return;
            setUserData(base);
            try {
              sessionStorage.setItem(cachedKey, JSON.stringify(base));
            } catch {}
            setQueryReady(true);
          } catch (e) {
            if (!isCurrentUserEvent(eventEpoch, sessionEpoch, cur.uid)) return;
            err("setDoc skeleton failed:", e?.code || e?.message);
          } finally {
            if (isCurrentUserEvent(eventEpoch, sessionEpoch, cur.uid)) {
              setLoading(false);
              setReady(true);
            }
          }
        }

        if (!isCurrentUserEvent(eventEpoch, sessionEpoch, cur.uid)) return;
        detachUserDocListener();
        unsubUserDocRef.current = onSnapshot(
          ref,
          (snapshot) => {
            if (
              cancelled ||
              userSessionEpochRef.current !== sessionEpoch ||
              activeUserDocUidRef.current !== cur.uid ||
              auth.currentUser?.uid !== cur.uid ||
              !snapshot.exists()
            ) {
              return;
            }

            const data = snapshot.data();
            setUserData(data);
            try {
              sessionStorage.setItem(cachedKey, JSON.stringify(data));
            } catch {}
            setLoading(false);
            setReady(true);
            setQueryReady(true);
          },
          (snapshotError) => {
            if (
              cancelled ||
              userSessionEpochRef.current !== sessionEpoch ||
              activeUserDocUidRef.current !== cur.uid
            ) {
              return;
            }
            if (
              snapshotError?.code === "permission-denied" &&
              !auth.currentUser
            ) {
              warn("onSnapshot permission-denied during auth gap → ignored");
              return;
            }
            err(
              "onSnapshot error:",
              snapshotError?.code || snapshotError?.name || "unknown",
              snapshotError?.message || snapshotError
            );
            setReady(true);
            setLoading(false);
          }
        );
        activeUserDocUidRef.current = cur.uid;

        setDoc(
          ref,
          { lastOnline: serverTimestamp(), mailTags: { online: true } },
          { merge: true }
        ).catch(() => {});
      });
    };

    startAuthListener();

    return () => {
      cancelled = true;
      if (unsubAuthRef.current) {
        try {
          unsubAuthRef.current();
        } catch {}
        unsubAuthRef.current = null;
      }
      authEventEpochRef.current += 1;
      userSessionEpochRef.current += 1;
      activeUserDocUidRef.current = null;
      detachUserDocListener();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, db, initialUser, initialCustomToken]); // do NOT depend on userData

  // Ensure the SSR/API cookies match the currently persisted Firebase user.
  // `initialUser === null` intentionally forces renewal: the browser may still
  // contain a syntactically present but invalid cookie from another environment.
  useEffect(() => {
    let cancelled = false;
    clearTimeout(cookieRetryTimerRef.current);

    const userUid = auth.currentUser?.uid || user?.uid;
    if (!userUid || isDealerLogoutInProgress()) return;

    const forceSessionRenewal =
      !initialUser?.uid || initialUser.uid !== userUid;
    const needsSessionCookie =
      forceSessionRenewal || !hasDealerFrontCookie();
    if (!needsSessionCookie || ensuredFrontCookieRef.current) return;
    if (forceSessionRenewal && hasDealerFrontCookie()) {
      clearDealerFrontCookie();
    }

    const ensureSessionCookie = async (attempt) => {
      if (
        cancelled ||
        isDealerLogoutInProgress() ||
        auth.currentUser?.uid !== userUid
      ) {
        return;
      }

      ensuredFrontCookieRef.current = true;
      try {
        await finishDealerLogin(api);
        if (
          cancelled ||
          isDealerLogoutInProgress() ||
          auth.currentUser?.uid !== userUid
        ) {
          return;
        }
        if (!hasDealerFrontCookie()) {
          throw new Error("Dealer front session cookie was not stored");
        }
      } catch (e) {
        ensuredFrontCookieRef.current = false;
        if (cancelled || isDealerLogoutInProgress()) return;

        if (attempt < SESSION_COOKIE_MAX_RETRIES) {
          cookieRetryTimerRef.current = setTimeout(
            () => ensureSessionCookie(attempt + 1),
            SESSION_COOKIE_RETRY_DELAY_MS * attempt
          );
          return;
        }
        err("finishDealerLogin failed:", e?.code || e?.message);
      }
    };

    ensureSessionCookie(1);

    return () => {
      cancelled = true;
      clearTimeout(cookieRetryTimerRef.current);
    };
  }, [user, api, initialUser?.uid]);

  // Public helpers
  const login = useCallback(async (mail, pw, link = false, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      clearDealerLogoutInProgress();
      await ensurePersistence(auth);

      const redirectTo =
        options.redirectTo === undefined ? "/marktplatz" : options.redirectTo;

      const res = await fetch(`${api}/auction/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
        body: JSON.stringify({
          email: mail.toLowerCase(),
          password: link ? undefined : pw,
          loginWithEmailLink: link,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Login failed");

      if (!link) {
        await signInWithCustomToken(auth, data.token);
        await finishDealerLogin(api); // sets HttpOnly + front cookie
        ensuredFrontCookieRef.current = true;
        if (redirectTo) {
          await push(redirectTo);
        }
      }
      return { success: true };
    } catch (e) {
      setError(e.message);
      err("login failed:", e.message);
      return { success: false, error: e };
    } finally {
      setLoading(false);
    }
  }, [api, push]);

  const logout = useCallback(async () => {
    setLoading(true);
    markDealerLogoutInProgress();
    clearDealerFrontCookie();
    clearTimeout(cookieRetryTimerRef.current);
    ensuredFrontCookieRef.current = false;
    try {
      await signOut(auth).catch(() => {});
      await fetch(`${api}/auction/logout`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        keepalive: true,
      }).catch(() => {});
      try {
        sessionStorage.clear();
      } catch {}
      prevUidRef.current = null;
      activeUserDocUidRef.current = null;
      authEventEpochRef.current += 1;
      userSessionEpochRef.current += 1;
      if (unsubUserDocRef.current) {
        try {
          unsubUserDocRef.current();
        } catch {}
        unsubUserDocRef.current = null;
      }
      setUser(null);
      setUserData(null);
      setClaims(null);
      setQueryReady(false);
      await push("/");
    } catch (e) {
      setError(e.message);
      err("logout failed:", e.message);
    } finally {
      setLoading(false);
    }
  }, [api, push]);

  const ctx = useMemo(
    () => ({
      user,
      userData,
      loading,
      authInitialized: ready,
      claims,
      canQuery,
      queryReady, // ⬅️ gate Firestore listeners with this + !!user
      error,
      isLoggedIn: !!user || !!userData, // SSR user keeps UI logged-in on first paint
      login,
      logout,
    }),
    [
      user,
      userData,
      loading,
      ready,
      claims,
      canQuery,
      queryReady,
      error,
      login,
      logout,
    ]
  );

  return (
    <AuthContext.Provider value={ctx}>
      {!blockTracking && isMarketingChecked && (
        <Script
          id="fb-pixel"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `!function(f,b,e,v,n,t,s){if(f.fbq)return;
            n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;
            s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
            (window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
            fbq('init','645534931725309');fbq('track','PageView');`,
          }}
        />
      )}
      {!blockTracking &&
        isAnalyticalChecked &&
        process.env.NODE_ENV === "production" && (
        <Script
          id="clarity"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
        if (!window.__CLARITY_LOADED__) {
          (function(c,l,a,r,i,t,y){
            if(c.clarity)return;
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "r78cayvlow");
          window.__CLARITY_LOADED__ = true;
        }
      `,
          }}
        />
      )}

      {children}
    </AuthContext.Provider>
  );
}
